import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { completeCashin, getAccountByPhone, CashinAccount } from '@shared/services/api/cashinService';
import {
  classifyRequestError,
  classifySuccessBody,
  logRequestError,
  type RequestErrorKind,
} from '@shared/utils/classifyRequestError';
import { getLastCorrelationId } from '@shared/utils/correlationId';
import { getEnv } from '@shared/utils/env';
import { getMessage } from '@shared/utils/messageUtils';

const INDETERMINATE_BUSINESS_CODES = new Set([
  'CORR/cashin-timeout',
  'CORR/cashin-in-progress',
  'CORR/cashin-pending-verification',
]);

const DUPLICATE_CONFIRMABLE_CODE = 'CORR/cashin-possible-duplicate';

const DUPLICATE_BLOCKED_CODE = 'CORR/cashin-duplicate-blocked';

const CONNECTION_CLOSED_CODE = 'CORR/cashin-connection-closed';

const UPSTREAM_TIMEOUT_MESSAGE = 'The upstream server is timing out';

const COMPLETE_CASHIN_URL = 'cashin/complete';

const isDuplicateOverrideEnabled = (): boolean =>
  getEnv().VITE_ENABLE_CASHIN_DUPLICATE_OVERRIDE === 'true';

const isIndeterminateCashin = (kind: RequestErrorKind, code?: string): boolean =>
  kind !== 'business' || INDETERMINATE_BUSINESS_CODES.has(code ?? '');

const isUpstreamTimeoutBody = (value: unknown): boolean =>
  Boolean(value) &&
  typeof value === 'object' &&
  (value as { message?: unknown }).message === UPSTREAM_TIMEOUT_MESSAGE;

const upstreamTimeoutStatus = (error: unknown): number | undefined => {
  if (!error || typeof error !== 'object' || !('response' in error)) {
    return undefined;
  }
  const status = (error as { response?: { status?: unknown } }).response?.status;
  return typeof status === 'number' ? status : undefined;
};

const isUpstreamTimeoutError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object' || !('response' in error)) {
    return false;
  }
  return isUpstreamTimeoutBody((error as { response?: { data?: unknown } }).response?.data);
};

const connectionClosedFromGateway = (retryableSafely: boolean): CashinRejectValue => ({
  message: getMessage('cashinUnconfirmedBody'),
  kind: 'business',
  code: CONNECTION_CLOSED_CODE,
  indeterminate: false,
  retryableSafely,
});

type CashinApiError = { code: string; message: string };

const duplicateCashinReject = (
  apiError: CashinApiError,
  responseData: unknown
): CashinRejectValue | null => {
  if (apiError.code === DUPLICATE_CONFIRMABLE_CODE) {
    if (isDuplicateOverrideEnabled()) {
      const dupData = responseData as { confirm_token?: string };
      return {
        message: apiError.message,
        kind: 'business',
        code: apiError.code,
        indeterminate: false,
        confirmToken: dupData?.confirm_token,
        isDuplicate: true,
      };
    }
    return {
      message: getMessage('cashinDuplicateBlocked'),
      kind: 'business',
      code: apiError.code,
      indeterminate: false,
      isDuplicate: true,
    };
  }
  // El backend ya bloqueó el duplicado: mismo texto y mismo tipo de alerta
  // que la rama anterior, para que el cajero vea siempre el mismo mensaje.
  if (apiError.code === DUPLICATE_BLOCKED_CODE) {
    return {
      message: getMessage('cashinDuplicateBlocked'),
      kind: 'business',
      code: apiError.code,
      indeterminate: false,
      isDuplicate: true,
    };
  }
  return null;
};

export interface CashinData {
  senderPhone?: string;
  receiverPhone?: string;
  amount?: number;
  request_id?: string;
  [key: string]: unknown;
}

export interface CashinTransfer {
  id?: string;
  domPayId?: number;
  account_from?: string;
  account_to?: string;
  account_to_name?: string;
  amount?: string;
  hashid?: string;
  err_code?: string | null;
  err_msg?: string | null;
  [key: string]: unknown;
}

export interface CashinRejectValue {
  message: string;
  kind: RequestErrorKind;
  indeterminate: boolean;
  retryableSafely?: boolean;
  code?: string;
  status?: number;
  confirmToken?: string;
  isDuplicate?: boolean;
  correlationId?: string;
}

interface CashinState {
  isProcessing: boolean;
  sender: CashinAccount | null;
  receiver: CashinAccount | null;
  transaction: CashinTransfer[] | null;
  replayed: boolean;
  requestId: string | null;
  error: CashinRejectValue | null;
}

export const verifySender = createAsyncThunk<CashinAccount, string, { rejectValue: CashinRejectValue }>(
  'cashin/verifySender',
  async (senderPhone, { rejectWithValue }) => {
    try {
      const response = await getAccountByPhone(senderPhone);

      if (response?.errors?.length > 0) {
        const apiError = response.errors[0];
        return rejectWithValue({
          message: apiError.message,
          kind: 'business',
          code: apiError.code,
          indeterminate: false,
        });
      }

      return response.data.data;
    } catch (error) {
      const classified = classifyRequestError(error);
      logRequestError('verifySender', classified, error);
      const backendMsg = classified.kind === 'business' ? classified.message : undefined;
      return rejectWithValue({
        message: backendMsg ?? 'No se pudo verificar al depositante. Intente nuevamente.',
        kind: classified.kind,
        code: classified.code,
        status: classified.status,
        indeterminate: false,
      });
    }
  }
);

export const verifyReceiver = createAsyncThunk<CashinAccount, string, { rejectValue: CashinRejectValue }>(
  'cashin/verifyReceiver',
  async (receiverPhone, { rejectWithValue }) => {
    try {
      const response = await getAccountByPhone(receiverPhone);

      if (response?.errors?.length > 0) {
        const apiError = response.errors[0];
        return rejectWithValue({
          message: apiError.message,
          kind: 'business',
          code: apiError.code,
          indeterminate: false,
        });
      }

      return response.data.data;
    } catch (error) {
      const classified = classifyRequestError(error);
      logRequestError('verifyReceiver', classified, error);
      const backendMsg = classified.kind === 'business' ? classified.message : undefined;
      // GET idempotente: reintentar es gratis, nunca es indeterminado.
      return rejectWithValue({
        message: backendMsg ?? 'No se pudo verificar el destino. Intente nuevamente.',
        kind: classified.kind,
        code: classified.code,
        status: classified.status,
        indeterminate: false,
      });
    }
  }
);

export const processCashin = createAsyncThunk<CashinTransfer[], CashinData, { rejectValue: CashinRejectValue }>(
  'cashin/processCashin',
  async (cashinData, { dispatch, rejectWithValue }) => {
    const retryableSafely = typeof cashinData.request_id === 'string' && cashinData.request_id.length > 0;

    try {
      const response = await completeCashin(cashinData);

      if (isUpstreamTimeoutBody(response)) {
        logRequestError('processCashin', {
          kind: 'business',
          code: CONNECTION_CLOSED_CODE,
          message: UPSTREAM_TIMEOUT_MESSAGE,
          status: 200,
        });
        return rejectWithValue(connectionClosedFromGateway(retryableSafely));
      }

      if (response?.errors?.length > 0) {
        const apiError = response.errors[0] as CashinApiError;
        const duplicateReject = duplicateCashinReject(apiError, response.data);
        if (duplicateReject) {
          return rejectWithValue(duplicateReject);
        }
        logRequestError('processCashin', {
          kind: 'business',
          code: apiError.code,
          message: apiError.message,
          status: 200,
        });
        return rejectWithValue({
          message: apiError.message,
          kind: 'business',
          code: apiError.code,
          indeterminate: isIndeterminateCashin('business', apiError.code),
          retryableSafely,
          correlationId: getLastCorrelationId(COMPLETE_CASHIN_URL),
        });
      }

      const malformed = classifySuccessBody(response, 'array');
      if (malformed) {
        logRequestError('processCashin', malformed, response);
        return rejectWithValue({
          message: getMessage('cashinIndeterminate'),
          kind: malformed.kind,
          status: malformed.status,
          indeterminate: true,
          retryableSafely,
          correlationId: getLastCorrelationId(COMPLETE_CASHIN_URL),
        });
      }

      dispatch(setCashinReplayed(Boolean(response.replayed)));
      return response.data;
    } catch (error) {
      if (isUpstreamTimeoutError(error)) {
        logRequestError('processCashin', {
          kind: 'business',
          code: CONNECTION_CLOSED_CODE,
          message: UPSTREAM_TIMEOUT_MESSAGE,
          status: upstreamTimeoutStatus(error),
        });
        return rejectWithValue(connectionClosedFromGateway(retryableSafely));
      }
      const classified = classifyRequestError(error);
      logRequestError('processCashin', classified, error);
      const backendMsg = classified.kind === 'business' ? classified.message : undefined;
      return rejectWithValue({
        message: backendMsg ?? getMessage('cashinIndeterminate'),
        kind: classified.kind,
        code: classified.code,
        status: classified.status,
        indeterminate: isIndeterminateCashin(classified.kind, classified.code),
        retryableSafely,
        correlationId: classified.correlationId,
      });
    }
  }
);

const initialState: CashinState = {
  isProcessing: false,
  sender: null,
  receiver: null,
  transaction: null,
  replayed: false,
  requestId: null,
  error: null,
};

const cashinSlice = createSlice({
  name: 'cashin',
  initialState,
  reducers: {
    resetCashinState: (state) => {
      state.sender = null;
      state.receiver = null;
      state.transaction = null;
      state.replayed = false;
      state.requestId = null;
      state.error = null;
    },
    setCashinReplayed: (state, action: PayloadAction<boolean>) => {
      state.replayed = action.payload;
    },
    setRecoveredCashin: (state, action: PayloadAction<{ transaction: CashinTransfer[]; requestId: string }>) => {
      state.transaction = action.payload.transaction;
      state.requestId = action.payload.requestId;
      state.replayed = true;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(verifySender.pending, (state) => {
        state.isProcessing = true;
        state.error = null;
      })
      .addCase(verifySender.fulfilled, (state, action) => {
        state.isProcessing = false;
        state.sender = action.payload;
      })
      .addCase(verifySender.rejected, (state, action) => {
        state.isProcessing = false;
        state.error = action.payload ?? null;
      })

      .addCase(verifyReceiver.pending, (state) => {
        state.isProcessing = true;
        state.error = null;
      })
      .addCase(verifyReceiver.fulfilled, (state, action) => {
        state.isProcessing = false;
        state.receiver = action.payload;
      })
      .addCase(verifyReceiver.rejected, (state, action) => {
        state.isProcessing = false;
        state.error = action.payload ?? null;
      })

      .addCase(processCashin.pending, (state) => {
        state.isProcessing = true;
        state.error = null;
      })
      .addCase(processCashin.fulfilled, (state, action) => {
        state.isProcessing = false;
        state.transaction = action.payload;
        state.requestId = action.meta?.arg?.request_id ?? null;
      })
      .addCase(processCashin.rejected, (state, action) => {
        state.isProcessing = false;
        state.error = action.payload ?? null;
      });
  },
});

export const { resetCashinState, setCashinReplayed, setRecoveredCashin } = cashinSlice.actions;

export default cashinSlice.reducer;
